import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { formatCurrency, useAddOrderItems } from '@/services/order-service';
import { OrderItemResponse, OrderStatus } from '@/types/order-type';
import { Edit2, Package, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useEditOrderItem } from '../sale/hooks/use-edit-order-item';
import { OrderItemEditDialog } from './OrderItemEditDialog';
import { OrderItemsEditor } from './OrderItemsEditor';
import { ProductDetailImag } from './ProductDetailImag';

interface ProductDetailsTableProps {
  orderItems: OrderItemResponse[];
  order?: any;
  onAfterAddItems?: () => void;
  onRefresh?: () => void;
}

export default function ProductDetailsTable({
  orderItems,
  order,
  onAfterAddItems,
  onRefresh,
}: ProductDetailsTableProps) {
  const [newItems, setNewItems] = useState<Array<{ variantId: string; quantity: number }>>([]);
  const [isAddItemsDialogOpen, setIsAddItemsDialogOpen] = useState(false);
  const addItemsMutation = useAddOrderItems();

  const orderStatus = order?.status || OrderStatus.DRAFT;

  const canEdit = useMemo(() => {
    const allowedStatuses = ['DRAFTED', 'CONFIRMED'];
    return allowedStatuses.includes(orderStatus);
  }, [orderStatus]);

  const { handleUpdateItem, editingItem, setEditingItem } = useEditOrderItem(order, () => {
    onRefresh?.();
  });

  const totalQuantity = useMemo(
    () => orderItems.reduce((sum, item) => sum + Number(item.quantity), 0),
    [orderItems],
  );

  const handleOpenAddItemsDialog = () => {
    setNewItems([]);
    setIsAddItemsDialogOpen(true);
  };

  const handleAddItems = async () => {
    if (!order?.id || newItems.length === 0) {
      setIsAddItemsDialogOpen(false);
      return;
    }

    try {
      const resp = await addItemsMutation.executeAsync({
        id: String(order.id),
        items: newItems,
      });

      if (resp.success) {
        toast({
          title: 'Success',
          description: 'Order items added successfully',
        });
        setIsAddItemsDialogOpen(false);
        setNewItems([]);
        onAfterAddItems?.();
        onRefresh?.();
      } else {
        throw new Error(resp.message || 'Failed to add items');
      }
    } catch (e) {
      toast({
        title: 'Error',
        description: e instanceof Error ? e.message : 'Failed to add items',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="sm:border sm:border-slate-200 sm:rounded-xl">
      <div className="p-0">
        {canEdit && order?.id && (
          <div className="flex items-center justify-between px-4 pt-4 pb-2 border-b border-slate-100">
            <div className="text-xs text-muted-foreground">
              Total items: <span className="font-semibold">{totalQuantity}</span>
            </div>
            <Button
              size="sm"
              onClick={handleOpenAddItemsDialog}
            >
              <Plus className="h-4 w-4" />
              Add items
            </Button>
          </div>
        )}

        {orderItems.length > 0 ? (
          <>
            {/* Mobile View - Card Layout */}
            <div className="md:hidden space-y-3">
              {orderItems.map((item) => {
                return (
                  <div
                    key={item.id}
                    className="border rounded-lg p-4 space-y-3 bg-card hover:shadow-md transition-shadow"
                  >
                    {/* Product Info with Image */}
                    <div className="flex gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <ProductDetailImag item={item} />
                          {canEdit && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingItem(item)}
                              className="gap-2 flex-shrink-0"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                              Edit
                            </Button>
                          )}
                        </div>
                        {item.sku && (
                          <Badge variant="secondary" className="text-xs mb-1">
                            {item.sku}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Price Details */}
                    <div className="grid grid-cols-2 gap-2 text-sm pt-2 border-t">
                      <div>
                        <p className="text-muted-foreground text-xs">Price</p>
                        <p className="font-medium tabular-nums">{formatCurrency(item.price)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Quantity</p>
                        <div className="font-medium">
                          <Badge variant="outline">{item.quantity}</Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop View - Compact Table */}
            <div className="hidden md:block">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="h-10 px-3 text-left align-middle font-medium text-muted-foreground text-sm">
                      Product
                    </th>
                    <th className="h-10 px-3 text-right align-middle font-medium text-muted-foreground text-sm w-24">
                      Price
                    </th>
                    <th className="h-10 px-3 text-center align-middle font-medium text-muted-foreground text-sm w-20">
                      Qty
                    </th>
                    {canEdit && (
                      <th className="h-10 px-3 text-center align-middle font-medium text-muted-foreground text-sm w-20">
                        Action
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {orderItems.map((item) => {
                    return (
                      <tr key={item.id} className="border-b transition-colors hover:bg-muted/50">
                        <td className="p-3 align-middle">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-0.5">
                              <ProductDetailImag item={item} />
                            </div>
                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                {item.sku && (
                                  <Badge variant="secondary" className="text-xs">
                                    {item.sku}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="p-3 align-middle text-right tabular-nums text-sm">
                          {formatCurrency(item.price)}
                        </td>

                        <td className="p-3 align-middle text-center">
                          <Badge variant="outline" className="font-semibold text-xs">
                            {item.quantity}
                          </Badge>
                        </td>

                        {canEdit && (
                          <td className="p-3 align-middle text-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingItem(item)}
                              className="h-8 w-8"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <OrderItemEditDialog
              item={editingItem}
              isOpen={!!editingItem}
              onClose={() => setEditingItem(null)}
              onSave={handleUpdateItem}
            />

            <Dialog open={isAddItemsDialogOpen} onOpenChange={setIsAddItemsDialogOpen}>
              <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0">
                <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b">
                  <DialogTitle>Add items to order</DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-hidden px-4 sm:px-6 py-4">
                  <OrderItemsEditor
                    value={newItems}
                    onChange={(items) => setNewItems(items)}
                    hideAllItems={false}
                  />
                </div>

                <DialogFooter className="px-4 sm:px-6 py-4 border-t flex-row gap-2 sm:gap-0">
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => {
                      setIsAddItemsDialogOpen(false);
                      setNewItems([]);
                    }}
                    className="flex-1 sm:flex-none"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={handleAddItems}
                    disabled={newItems.length === 0}
                    className="flex-1 sm:flex-none"
                  >
                    Save items
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        ) : (
          <div className="p-6">
            <Alert>
              <Package className="h-4 w-4" />
              <AlertDescription>No items found in this order.</AlertDescription>
            </Alert>
          </div>
        )}
      </div>
    </div>
  );
}
